import React, {
    useCallback,
    // useCallback,
    useEffect,
    useState,
} from 'react'
import ComplianceGraph from './Graph'
import {
    IReportData,
    IAppendixRef,
    ITaxYearDataRes,
    ITaxYearData,
    ITaxYear,
    ComplianceReport,
    IReportDataDate,
} from './types'
import debounce from 'lodash.debounce'
import { Alert, Button, Divider, Popconfirm, Space, Spin, Tooltip } from 'antd'
import './style.less'
import {
    SyncOutlined,
    DownloadOutlined,
    QuestionCircleOutlined,
    LockOutlined,
    UnlockOutlined,
} from '@ant-design/icons'
import { Camera } from '../../components/Camera'
import { APIFunctions } from '../../lib/apiFunctions'
import { IPeriodCache } from '../../lib/interfaces'
import FileSaver from 'file-saver'
import LockModal from './LockModal'
import ComplianceDatesView from './ComplianceDatesView'
import { isSameDay, toDate } from 'date-fns'
// import { createStyles, makeStyles, Popover, Theme } from '@mui/material'
// import { WhereAbouts } from '../../components/CalendarDayMenu/WhereAbouts'

export const cellHeight = 31
const listHeight = 400

// const useStyles = makeStyles((theme: Theme) =>
//     createStyles({
//         whereAboutsContainer: {
//             padding: theme.spacing(2),
//         },
//     })
// )

export default function ReportView({
    taxYear,
    reportId,
    getTaxYearData,
    loadOverviewData,
    switchToTab,
    renderMeta,
    apiFunctions,
    refreshReportDatesData,
    isRefreshing,
}: {
    apiFunctions: APIFunctions
    taxYear: ITaxYear
    getTaxYearData: (taxYear: ITaxYear, forceFetch?: boolean) => Promise<ITaxYearDataRes>
    loadOverviewData: (postSwitchToTab?: string) => Promise<ITaxYear[]>
    switchToTab: (tab: string) => void
    reportId: string
    renderMeta: (llPeriod: IPeriodCache) => any
    refreshReportDatesData: (reportId: string) => Promise<void>
    isRefreshing: boolean
}) {
    // const classes = useStyles()
    const [lockModalVisible, setLockModalVisible] = useState<boolean>(false)
    const [dataHasChanged, setDataHasChanged] = useState<boolean>(false)
    const [reportMeta, setReportMeta] = useState<ComplianceReport>()
    const [visibleInterval, setVisibleInterval] = useState<(number | Date)[]>([])
    const [currentDate, setCurrentDate] = useState<Date | number | null>(null)
    const [appendixVisible, setAppendixVisible] = useState<boolean>(false)
    // const [anchorUserLocation, setAnchorUserLocation] = useState<HTMLButtonElement | null>(null)
    // const [whereAboutsDropdownOpen, setWhereAboutsDropdownOpen] = useState<boolean>(true)
    const [isLoading, setIsLoading] = useState<boolean>(true)
    const [llPeriod, setLLPeriod] = useState<IPeriodCache | null>(null)
    const [isDownloading, setIsDownloading] = useState<boolean>(false)
    const [reportData, setReportData] = useState<IReportData>()
    const [isFinishedLocking, setIsFinishedLocking] = useState<boolean>(false)
    const [intervalRef, setIntervalRef] = useState<any>(null)

    useEffect(() => {
        if (!reportMeta) return
        if (reportMeta.ll_period_data) {
            setLLPeriod(reportMeta.ll_period_data)
            return
        }
        apiFunctions.getLLPeriod(reportMeta.period_id).then((data) => {
            setLLPeriod(data.ll_period)
        })
    }, [reportMeta, apiFunctions])

    async function checkDownloadRdy(): Promise<boolean> {
        if (!reportMeta || !reportMeta.is_locked) return false
        const isFinished = await apiFunctions.getLockedReportUploadFinished(
            reportMeta.id,
            reportMeta.locked_report_id
        )
        setIsFinishedLocking(isFinished)
        return isFinished
    }

    async function setupDownloadInterval() {
        const isReady = await checkDownloadRdy()
        if (isReady) return
        const interval = setInterval(checkDownloadRdy, 5000)
        setIntervalRef(interval)
    }

    useEffect(() => {
        if (!reportMeta || !reportMeta.is_locked) return
        if (intervalRef) return
        setupDownloadInterval()
    }, [reportMeta, apiFunctions])

    useEffect(() => {
        if (isFinishedLocking && intervalRef) {
            clearInterval(intervalRef)
        }
    }, [intervalRef, isFinishedLocking])

    const refreshData = React.useCallback(async () => {
        if (!reportId) {
            return
        }
        const data = await getTaxYearData(taxYear)
        if (!data) return
        let taxYearReportData: ITaxYearData | undefined = data.tax_year_reports.find(
            (tyr) => tyr.id === reportId
        )
        if (!taxYearReportData) {
            const newData = await getTaxYearData(taxYear, true)
            if (!newData) return
            taxYearReportData = newData.tax_year_reports.find((tyr) => tyr.id === reportId)
        }
        if (!taxYearReportData) return
        setReportData(taxYearReportData.report_data)
        setReportMeta(taxYearReportData.report_meta)
    }, [setReportData, setReportMeta, getTaxYearData])

    useEffect(() => {
        if (!refreshData || isRefreshing) return
        refreshData()
    }, [taxYear, reportId, isRefreshing])

    useEffect(() => {
        setIsLoading(!reportData)
    }, [reportData])

    const debouncedScroll = useCallback(
        // calculate start and end date in O(1) time instead of slicing list in O(n) time
        debounce((e) => {
            const start = Math.max(0, Math.round(e.target?.scrollTop / cellHeight) - 1)
            const end = Math.min(
                (reportData?.dates?.length || 1) - 1,
                start + Math.round(listHeight / cellHeight)
            )
            if (!reportData || !reportData?.dates || reportData?.dates.length < 2) {
                return
            }
            const startDate: any =
                reportData?.dates.length <= start
                    ? reportData?.dates[reportData?.dates.length - 2]?.date
                    : reportData?.dates[start]?.date
            const endDate: any =
                reportData?.dates.length <= end
                    ? reportData?.dates[reportData?.dates.length - 1]
                    : reportData?.dates[end]?.date
            if (!(startDate && endDate)) return
            setVisibleInterval([startDate, endDate])
        }, 100),
        [reportData]
    )

    function scrollToDate(date: Date) {
        const table = document.getElementsByClassName('report-date-list-scroller')
        if (!(table && table[0])) return
        reportData?.dates?.find((d, idx) => {
            if (isSameDay(toDate(new Date(d.date)), toDate(new Date(date)))) {
                table[0].scrollTo({
                    top: Math.max(
                        0,
                        idx * cellHeight - Math.round((cellHeight * listHeight) / 2 / cellHeight)
                    ),
                    behavior: 'smooth',
                })
                return true
            }
            return false
        })
    }

    function showBilag(appendixRef: IAppendixRef[], date: number | Date) {
        if (
            !reportMeta ||
            (reportMeta.is_locked && !appendixRef) ||
            (reportMeta.is_locked && appendixRef.filter((r) => r.include_report).length === 0)
        )
            return
        setCurrentDate(date)
        setAppendixVisible(true)
    }

    // const userLocationsVisible = Boolean(anchorUserLocation)
    // const userLocationsId = userLocationsVisible ? 'simple-popover' : undefined

    // function showUserLocations(
    //     userLocations,
    //     date: number | Date,
    //     event: React.MouseEvent<HTMLButtonElement>
    // ) {
    //     if (reportMeta.is_locked) {
    //         return
    //     }
    //     setCurrentDate(date)
    //     setAnchorUserLocation(event.currentTarget)
    // }

    function reportChange(action: any) {
        setReportData({
            ...reportData,
            dates: reportData.dates.map((d: IReportDataDate) => {
                if (d.date === action.targetDate) {
                    let newAppendix = []
                    switch (action.action) {
                        case 'fileListChange':
                            newAppendix = action.newFileList.map((nf) => {
                                if (!Object.keys(nf).includes('include_report')) {
                                    nf.include_report = true
                                }
                                if (!Object.keys(nf).includes('id')) {
                                    nf.id = nf.uid
                                }
                                return nf
                            })
                            break
                        case 'delete':
                            newAppendix = d.appendixref.filter((r) => r.id !== action.fileId)
                            break
                        case 'include':
                        case 'exclude':
                            newAppendix = d.appendixref.map((r: IAppendixRef) => {
                                return r.id === action.fileId
                                    ? {
                                          ...r,
                                          include_report: action.isIncluded,
                                      }
                                    : r
                            })
                            break
                        case 'newLocations':
                            return {
                                ...d,
                                user_locations: action.newLocations,
                            }
                    }
                    return {
                        ...d,
                        appendixref: newAppendix,
                    }
                }
                return d
            }),
        })
    }

    const LoadingComp = <SyncOutlined style={{ fontSize: 100 }} spin />

    if (isLoading || isRefreshing) {
        return (
            <Spin spinning={true} indicator={LoadingComp} className="report-spinner">
                <div className="report-container" />
            </Spin>
        )
    }

    async function unlockReport() {
        setIsLoading(true)
        if (await apiFunctions.removeLockFromReport(reportMeta.id, reportMeta?.locked_report_id)) {
            await loadOverviewData(
                `taxYearReport;;;${reportMeta.ll_tax_year_id}:::${reportMeta.id}`
            )
        }
    }

    function downloadReport() {
        if (!reportMeta || !reportMeta.report_url) return
        setIsDownloading(true)
        FileSaver.saveAs(reportMeta.report_url, 'compliance-report.zip')
        setIsDownloading(false)
    }

    function renderButtons() {
        if (!reportMeta) return null
        if (reportMeta.is_locked) {
            return (
                <Space size="large" style={{ justifyContent: 'flex-end' }}>
                    {/* <QuestionCircleOutlined /> */}
                    {reportMeta.report_url && (
                        <Tooltip
                            title={isFinishedLocking ? 'Download report' : 'Preparing download'}
                        >
                            <Button
                                type="primary"
                                icon={<DownloadOutlined />}
                                onClick={downloadReport}
                                loading={isDownloading || !isFinishedLocking}
                                disabled={!isFinishedLocking}
                            >
                                Download report
                            </Button>
                        </Tooltip>
                    )}
                    <Popconfirm
                        title={
                            taxYear.is_locked
                                ? 'You can not unlock reports within a locked Tax Year'
                                : 'Confirm unlock report? '
                        }
                        icon={<QuestionCircleOutlined style={{ color: 'red' }} />}
                        onConfirm={taxYear.is_locked ? () => {} : unlockReport}
                    >
                        <Button disabled={taxYear.is_locked} danger icon={<UnlockOutlined />}>
                            Unlock Report
                        </Button>
                    </Popconfirm>
                </Space>
            )
        } else {
            return (
                <Space size="large" style={{ justifyContent: 'flex-end' }}>
                    <Button
                        type="primary"
                        icon={<LockOutlined />}
                        onClick={() => {
                            setLockModalVisible(true)
                        }}
                    >
                        Lock report
                    </Button>
                </Space>
            )
        }
    }

    return (
        <>
            {dataHasChanged && (
                <Alert
                    message="Report data is out of sync!"
                    description={
                        <>
                            Some changes you have made are not shown on the graph.
                            <br />
                            You can continue with your chages and click 'refresh' when you are done.
                        </>
                    }
                    type="error"
                    showIcon
                    closeText={
                        <>
                            <Button
                                onClick={() => {
                                    refreshReportDatesData(reportMeta.id)
                                }}
                                type="primary"
                                style={{ marginRight: 20 }}
                            >
                                refresh
                            </Button>
                        </>
                    }
                />
            )}
            <div className="report-tab-header">
                <div className="report-header-nav">
                    <h1>
                        Complete report - {taxYear.tax_year} - {taxYear.tax_year_name}
                    </h1>
                    {renderButtons()}
                </div>
                <Divider />
            </div>
            <div className="report-container">
                {renderMeta(llPeriod)}
                <Divider />
                <ComplianceDatesView
                    debouncedScroll={debouncedScroll}
                    data={reportData}
                    showBilag={showBilag}
                    // showUserLocations={showUserLocations}
                    apiFunctions={apiFunctions}
                    setDataHasChanged={setDataHasChanged}
                    userId={taxYear.ll_user_id}
                    reportMeta={reportMeta}
                    listHeight={listHeight}
                    reportChange={reportChange}
                    taxYear={taxYear}
                />
                <Divider />
                <div>
                    <ComplianceGraph
                        scrollToDate={scrollToDate}
                        data={reportData}
                        visibleInterval={visibleInterval}
                    />
                </div>
                <Divider />
            </div>
            <Camera
                apiFunctions={apiFunctions}
                userId={taxYear.ll_user_id}
                allowChanges={!!reportMeta && !reportMeta.is_locked}
                dateClicked={currentDate}
                visible={appendixVisible}
                setVisible={setAppendixVisible}
                docDates={[]}
                setDocDates={(props) => {}}
                title={
                    <h3>{currentDate}</h3>
                    // <h3>{currentDate && formatText(toDate(currentDate), "dd. MMM")}</h3>
                }
                subTitle={
                    'Use the switch to include or exclude ' +
                    'documentation in compliance reports. ' +
                    'Note that shared reports only show included files.'
                }
                reportChange={reportChange}
            />
            {/* <Popover
                id={userLocationsId}
                open={userLocationsVisible}
                anchorEl={anchorUserLocation}
                onClose={() => setAnchorUserLocation(null)}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}
            >
                <div className={classes.whereAboutsContainer}>
                    <WhereAbouts
                        dateClicked={currentDate}
                        apiFunctions={apiFunctions}
                        userId={taxYear.ll_user_id}
                        dropdownOpen={whereAboutsDropdownOpen}
                        setDropdownOpen={setWhereAboutsDropdownOpen}
                        reportChange={reportChange}
                    />
                </div>
            </Popover> */}
            <LockModal
                reportMeta={reportMeta}
                llPeriod={llPeriod}
                visible={lockModalVisible}
                setVisible={setLockModalVisible}
                loadOverviewData={loadOverviewData}
                switchToTab={switchToTab}
                apiFunctions={apiFunctions}
            />
        </>
    )
}
